import React, { useCallback, useMemo, useState } from 'react';
import { useFundedSummary } from '../../hooks/useFundedSummary';
import { useQuery } from '@tanstack/react-query';
import { toast as message } from 'sonner';
import { useRecordNavigator, useRecordParam } from '../../hooks/useRecordNavigator';
import { useFillGridHeight } from '../../hooks/useFillGridHeight';
import { useTableActiveFilter, useTableZoom } from '../../utils/tableViewSettings';
import type { ActiveFilterValue } from '../../utils/tableViewSettings';
import { GridZoomControl } from '../ui/GridZoomControl';
import type { ColDef } from 'ag-grid-community';
import { supabaseClient } from '../../utils/supabase';
import { startDateWithTenure } from '../../utils/dateFormat';
import { useMyCapabilities } from '../../utils/metadata/capabilities';
import { getEmailValidationError, getPhoneValidationError } from '../../utils/validation';
import { useGlobalMetadata } from '../../utils/providers';
import { prefetchRecordDestinationIfAbsent } from '../../utils/recordDestinationPrefetch';
import WorkspaceGrid from '../WorkspaceGrid/WorkspaceGrid';
import { queryClient } from '../../utils/constants';
import { LivePicklistCell, MERCHANT_FEEDBACK_EDIT } from '../ReportPage/LivePicklistCell';
import { useTableInlineEdit } from '../WorkspaceGrid/useTableInlineEdit';
import { EditModeToggle, UndoLastEditButton } from '../TableGrid/EditModeToggle';
import { ListLeaderboard } from '../ListLeaderboard/ListLeaderboard';
import FunderSettings, { type FunderSettingsData } from './FunderSettings';
import CreateListRecordButton from '../CreateListRecordButton';
import EnhancedDialog from '../EnhancedDialog';
import { ValidatedEmailInput } from '../ValidatedEmailInput';
import { ValidatedPhoneInput } from '../ValidatedPhoneInput';
import FundedDashboard from '../admin/reporting/FundedDashboard';
import '../RepPage/RepPage.css';
import './FunderPage.css';
import { SHEET_DEAL_SELECT, sheetDealColumns, sheetDealFields, type SheetDealFields } from '../../utils/sheetDealColumns';

type Decision = 'funded' | 'approved' | 'declined' | 'no_response';

interface FunderRecord {
  id: number;
  name: string;
  active: boolean;
  /** REG-902: deals to this funder go out as Region. */
  region: boolean;
  lifetimeFundedAmount: number;
  fundedCount: number;
  grossCommission: number;
  approvalToFundedRatio: number;
  totalUniqueMerchants: number;
  totalSubmissions: number;
  totalDeclines: number;
  /** REG-828: approved, contracts or funded submissions (performing), their amount, and the declined amount. */
  totalApprovals: number;
  totalApprovedAmount: number;
  totalDeclinedAmount: number;
  isoRep: string;
  phone: string;
  email: string;
  submissionEmail: string;
  routingMode: string;
  restrictions: string;
  notes: string;
  createdAt: string | null;
  settings: FunderSettingsData;
}

interface FunderSubmission extends SheetDealFields {
  id: number;
  merchantId: number | null;
  merchant: string;
  rep: string;
  decision: Decision;
  amount: number;
  factor: number | null;
  term: number | null;
  declineReason: string;
  reason: string;
  date: string | null;
  timestamp: number;
  dateFunded: string | null;
  approvalDate: string | null;
  /** This submission is its merchant's canonical primary approval. */
  isPrimary: boolean;
  /** merchant.merchant_response, set from the Approvals tab's live dropdown (REG-740). */
  merchantResponse: string | null;
  merchantFirstName: string;
  merchantEmail: string;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const APPROVED = new Set(['approved', 'contracts out', 'contracts in']);
const AMOUNTS = [
  { value: 'all', label: 'Any amount', min: 0, max: Infinity },
  { value: 'under25', label: '< $25k', min: 0, max: 25_000 },
  { value: '25to50', label: '$25k - $50k', min: 25_000, max: 50_000 },
  { value: '50to100', label: '$50k - $100k', min: 50_000, max: 100_000 },
  { value: 'over100', label: '> $100k', min: 100_000, max: Infinity },
];

function decision(status: string | null): Decision {
  const value = (status || '').toLowerCase();
  if (value === 'funded') return 'funded';
  if (APPROVED.has(value)) return 'approved';
  if (value === 'declined') return 'declined';
  return 'no_response';
}

function dollars(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
}

function shortDollars(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(value >= 10_000_000 ? 0 : 1)}M`;
  if (value >= 1_000) return `$${Math.round(value / 1_000)}k`;
  return dollars(value);
}

function shortDate(value: string | null): string {
  return value ? new Intl.DateTimeFormat('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' }).format(new Date(value)) : '-';
}

function tenure(value: string | null): string {
  if (!value) return '-';
  const months = Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / 2_629_800_000));
  return months < 12 ? `${months} mo` : `${Math.floor(months / 12)}y${months % 12 ? ` ${months % 12}mo` : ''}`;
}

function stats(rows: FunderSubmission[]) {
  const funded = rows.filter((row) => row.decision === 'funded');
  const approved = rows.filter((row) => row.decision === 'approved');
  const declined = rows.filter((row) => row.decision === 'declined');
  const noResponse = rows.filter((row) => row.decision === 'no_response');
  const approvedUnits = funded.length + approved.length;
  const decided = approvedUnits + declined.length;
  const approvalAmount = [...funded, ...approved].reduce((sum, row) => sum + row.amount, 0);
  return {
    total: rows.length, funded: funded.length, approved: approved.length, declined: declined.length,
    noResponse: noResponse.length, approvedUnits, decided,
    fundedAmount: funded.reduce((sum, row) => sum + row.amount, 0), approvalAmount,
    average: approvedUnits ? Math.round(approvalAmount / approvedUnits) : 0,
    approvalRate: decided ? Math.round(approvedUnits / decided * 100) : 0,
    fundedRate: approvedUnits ? Math.round(funded.length / approvedUnits * 100) : 0,
  };
}

export async function loadFunders(): Promise<FunderRecord[]> {
  const supabase = supabaseClient as any;
  const [funderResult, lifetimeResult] = await Promise.all([
    supabase.from('funder_deep_view')
      .select('id,name,is_active,rep1_first_name,rep1_last_name,rep1_phone,rep1_cell,rep1_email,submission_email,note,misc,created_at,'
        + 'routing_mode,additional_submission_emails,submission_cc_emails,portal_link,'
        + 'prohibited_states,prohibited_industries,drive_folder_link,website,state_overrides,'
        + 'is_region,region_submission_email')
      .order('name'),
    supabase.from('funder_lifetime_summary_v')
      .select('funder,lifetime_funded_amount,approval_to_funded_ratio,total_unique_merchants,total_submissions,total_declines,total_funded,gross_commission,total_approved,total_approved_amount,total_declined_amount'),
  ]);
  if (funderResult.error) throw funderResult.error;
  if (lifetimeResult.error) throw lifetimeResult.error;

  const lifetimeByFunder = new Map<number, any>(
    (lifetimeResult.data || []).map((row: any) => [Number(row.funder), row])
  );

  return ((funderResult.data || []) as any[]).flatMap((row) => {
    if (row.id == null) return [];
    const lifetime = lifetimeByFunder.get(Number(row.id)) || {};
    return [{
    id: row.id,
    name: row.name || `Funder #${row.id}`,
    active: row.is_active !== false,
    region: row.is_region === true,
    lifetimeFundedAmount: Number(lifetime.lifetime_funded_amount) || 0,
    fundedCount: Number(lifetime.total_funded) || 0,
    grossCommission: Number(lifetime.gross_commission) || 0,
    approvalToFundedRatio: Number(lifetime.approval_to_funded_ratio) || 0,
    totalUniqueMerchants: Number(lifetime.total_unique_merchants) || 0,
    totalSubmissions: Number(lifetime.total_submissions) || 0,
    totalDeclines: Number(lifetime.total_declines) || 0,
    totalApprovals: Number(lifetime.total_approved) || 0,
    totalApprovedAmount: Number(lifetime.total_approved_amount) || 0,
    totalDeclinedAmount: Number(lifetime.total_declined_amount) || 0,
    isoRep: [row.rep1_first_name, row.rep1_last_name].filter(Boolean).join(' '),
    phone: row.rep1_phone || row.rep1_cell || '',
    email: row.rep1_email || '',
    submissionEmail: row.submission_email || '',
    routingMode: row.routing_mode || '',
    restrictions: [row.prohibited_states, row.prohibited_industries].filter(Boolean).join(' · '),
    notes: row.note || row.misc || '',
    createdAt: row.created_at,
    settings: {
      id: row.id,
      routing_mode: row.routing_mode,
      submission_email: row.submission_email,
      additional_submission_emails: row.additional_submission_emails,
      submission_cc_emails: row.submission_cc_emails,
      rep1_email: row.rep1_email,
      rep1_first_name: row.rep1_first_name,
      rep1_last_name: row.rep1_last_name,
      rep1_phone: row.rep1_phone,
      portal_link: row.portal_link,
      prohibited_states: row.prohibited_states,
      prohibited_industries: row.prohibited_industries,
      drive_folder_link: row.drive_folder_link,
      website: row.website,
      note: row.note,
      state_overrides: row.state_overrides,
      is_region: row.is_region === true,
      region_submission_email: row.region_submission_email,
    },
  }];
  });
}

async function loadFunderSubmissions(funderId: number): Promise<FunderSubmission[]> {
  const supabase = supabaseClient as any;
  const raw: any[] = [];
  for (let offset = 0; ; offset += 1000) {
    const { data, error } = await supabase.from('funder_submission_activity_v')
      .select(`submission,merchant,merchant_label,main_rep_label,status,amount,factor_rate,term,reason_for_decline,missing_documents,activity_date,date_funded,approval_date,is_primary_approval,merchant_response,merchant_first_name,merchant_email,${SHEET_DEAL_SELECT}`)
      .eq('funder', funderId).order('submission', { ascending: true }).range(offset, offset + 999);
    if (error) throw error;
    raw.push(...(data || []));
    if (!data || data.length < 1000) break;
  }
  return raw.flatMap((row): FunderSubmission[] => row.submission == null ? [] : [{
    id: row.submission, merchantId: row.merchant, merchant: row.merchant_label || '-',
    rep: row.main_rep_label || 'Unassigned',
    decision: decision(row.status), amount: Number(row.amount) || 0, factor: row.factor_rate,
    term: row.term, declineReason: row.reason_for_decline || '',
    reason: row.reason_for_decline || row.missing_documents || '',
    date: row.activity_date, timestamp: row.activity_date ? new Date(row.activity_date).getTime() : 0,
    dateFunded: row.date_funded ?? null,
    approvalDate: row.approval_date ?? null,
    isPrimary: row.is_primary_approval === true,
    merchantResponse: row.merchant_response ?? null,
    merchantFirstName: row.merchant_first_name || '',
    merchantEmail: row.merchant_email || '',
    ...sheetDealFields(row),
  }]).sort((a, b) => b.timestamp - a.timestamp);
}

function ChipEditButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button className="rep-chip-edit" onClick={onClick} title={label} aria-label={label}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
    </button>
  );
}

function FunderContactDialog({ funder, onClose, onSaved }: { funder: FunderRecord; onClose: () => void; onSaved: () => Promise<unknown> }) {
  const [firstName, setFirstName] = useState(funder.settings.rep1_first_name || '');
  const [lastName, setLastName] = useState(funder.settings.rep1_last_name || '');
  const [email, setEmail] = useState(funder.email);
  const [phone, setPhone] = useState(funder.settings.rep1_phone || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const validationError = getEmailValidationError(email) || getPhoneValidationError(phone);
    if (validationError) {
      message.error(validationError);
      return;
    }
    setSaving(true);
    try {
      // .select('id') is the RLS probe: funder UPDATE requires edit_deals, and
      // a filtered zero-row result would otherwise look like a success.
      const { data, error } = await (supabaseClient as any)
        .from('funder')
        .update({
          rep1_first_name: firstName.trim() || null,
          rep1_last_name: lastName.trim() || null,
          rep1_email: email.trim() || null,
          rep1_phone: phone.trim() || null,
        })
        .eq('id', funder.id)
        .select('id');
      if (error) throw error;
      if (!data?.length) throw new Error('You may not have permission to edit funder contact info.');
      await onSaved();
      message.success('Contact info updated.');
      onClose();
    } catch (error) {
      message.error((error as Error).message || 'Unable to save contact info.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <EnhancedDialog isOpen onClose={onClose} title={`Edit ISO rep contact — ${funder.name}`} canOutsideClickClose={!saving}>
      <div className="sky-dialog-body rep-contact-dialog">
        <label className="rep-contact-field">
          First name
          <input value={firstName} onChange={(event) => setFirstName(event.target.value)} disabled={saving} />
        </label>
        <label className="rep-contact-field">
          Last name
          <input value={lastName} onChange={(event) => setLastName(event.target.value)} disabled={saving} />
        </label>
        <label className="rep-contact-field">
          Email
          <ValidatedEmailInput value={email} onChange={setEmail} onValidChange={() => {}} disabled={saving} />
        </label>
        <label className="rep-contact-field">
          Phone
          <ValidatedPhoneInput value={phone} onChange={setPhone} onValidChange={() => {}} disabled={saving} />
        </label>
      </div>
      <div className="sky-dialog-footer">
        <button className="sky-btn sky-btn-ghost" disabled={saving} onClick={onClose}>Cancel</button>
        <button className="sky-btn sky-btn-primary" disabled={saving} onClick={() => void save()}>{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </EnhancedDialog>
  );
}

function FunderRenameDialog({ funder, onClose, onSaved }: { funder: FunderRecord; onClose: () => void; onSaved: () => Promise<unknown> }) {
  const [name, setName] = useState(funder.name);
  const [saving, setSaving] = useState(false);
  const trimmed = name.trim();
  const unchanged = trimmed === funder.name;

  const save = async () => {
    if (!trimmed) {
      message.error('Funder name is required.');
      return;
    }
    setSaving(true);
    try {
      // Same RLS probe as the contact dialog: funder UPDATE requires edit_deals
      // and a filtered zero-row result would otherwise look like a success.
      // The funder_before trigger queues the deal labels (submission.display_name)
      // for this funder; a cron rewrites them within a minute (REG-808).
      const { data, error } = await (supabaseClient as any)
        .from('funder')
        .update({ name: trimmed })
        .eq('id', funder.id)
        .select('id');
      if (error) throw error;
      if (!data?.length) throw new Error('You may not have permission to rename funders.');
      await onSaved();
      message.success(`Renamed to ${trimmed}. Deal labels update within a minute.`);
      onClose();
    } catch (error) {
      message.error((error as Error).message || 'Unable to rename funder.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <EnhancedDialog isOpen onClose={onClose} title={`Rename funder — ${funder.name}`} canOutsideClickClose={!saving}>
      <div className="sky-dialog-body rep-contact-dialog">
        <label className="rep-contact-field">
          Funder name
          <input value={name} onChange={(event) => setName(event.target.value)} disabled={saving} autoFocus maxLength={200} />
        </label>
      </div>
      <div className="sky-dialog-footer">
        <button className="sky-btn sky-btn-ghost" disabled={saving} onClick={onClose}>Cancel</button>
        <button className="sky-btn sky-btn-primary" disabled={saving || !trimmed || unchanged} onClick={() => void save()}>{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </EnhancedDialog>
  );
}

function Loading({ error }: { error?: Error | null }) {
  return <div className="rep-page-state">{error ? <><strong>Unable to load funders</strong><span>{error.message}</span></> : <><span className="rep-spinner"/><span>Loading funders...</span></>}</div>;
}

const FUNDER_STATUS_LABELS: Record<Decision, string> = { funded: 'Funded', approved: 'Approved', declined: 'Declined', no_response: 'No Response' };

const FUNDER_LIST_KEYS = [['funder-target-page']];

// REG-828: default columns and order from the COLUMNS sheet (FUNDER). Columns
// the sheet does not list stay available, hidden, in the column chooser.
const FUNDER_COLUMNS: ColDef<FunderRecord>[] = [
  { headerName: 'FUNDER', field: 'name', flex: 180, minWidth: 150, context: { editField: 'name' },
    cellRenderer: (p: { data?: FunderRecord }) => p.data ? (
      <div className="funder-name"><span>{p.data.name.slice(0, 2).toUpperCase()}</span><b>{p.data.name}</b></div>
    ) : null },
  // REG-902: Region funders get their deals sent as Region (Funder Settings).
  { headerName: 'REGION', colId: 'region', flex: 100, minWidth: 100, filter: 'agSetColumnFilter',
    headerTooltip: 'Deals to this funder go out as Region (from deals@regioncapitalgroup.com)',
    valueGetter: (p) => (p.data ? (p.data.region ? 'Region' : 'Skyline') : ''),
    cellRenderer: (p: { data?: FunderRecord }) => p.data
      ? <span className={`funder-brand-pill ${p.data.region ? 'region' : ''}`}>{p.data.region ? 'Region' : 'Skyline'}</span>
      : null },
  { headerName: 'START DATE / TENURE', colId: 'createdAt', field: 'createdAt', flex: 150, minWidth: 160,
    valueFormatter: (p) => startDateWithTenure(p.value) },
  { headerName: 'ISO REP', field: 'isoRep', flex: 130, minWidth: 110, cellClass: 'primary-cell', valueFormatter: (p) => p.value || '-' },
  // Inline edits (REG-739) write the raw funder columns kept under `settings`.
  { headerName: 'ISO REP NUMBER', field: 'phone', flex: 130, minWidth: 130, valueFormatter: (p) => p.value || '-',
    context: { editField: 'rep1_phone', rowField: 'settings.rep1_phone' } },
  { headerName: 'ISO REP EMAIL', field: 'email', flex: 200, minWidth: 150, cellClass: 'rep-email', valueFormatter: (p) => p.value || '-',
    context: { editField: 'rep1_email', rowField: 'settings.rep1_email' } },
  { headerName: 'SUBMISSION EMAIL', colId: 'submissionEmail', flex: 230, minWidth: 170,
    context: { editField: 'submission_email', rowField: 'settings.submission_email' },
    valueGetter: (p) => p.data?.submissionEmail || p.data?.email || '-',
    cellRenderer: (p: { data?: FunderRecord }) => {
      if (!p.data) return null;
      const display = p.data.submissionEmail || p.data.email || '-';
      const same = !p.data.submissionEmail || p.data.submissionEmail.trim().toLowerCase() === p.data.email.trim().toLowerCase();
      return <div className="funder-email-cell"><div className="funder-sub-email">{display}</div><small className={same ? 'same' : 'separate'}>{same ? 'SAME AS REP' : 'SEPARATE'}</small></div>;
    } },
  { headerName: 'ROUTING', field: 'routingMode', flex: 120, minWidth: 100, filter: 'agSetColumnFilter',
    context: { editField: 'routing_mode', rowField: 'settings.routing_mode' },
    cellRenderer: (p: { value?: string }) => p.value ? <span className={`funder-routing-pill ${p.value === 'Portal' ? 'portal' : ''}`}>{p.value}</span> : <span className="funder-routing-pill unset">—</span> },
  { headerName: 'RESTRICTIONS', field: 'restrictions', flex: 160, minWidth: 120, cellClass: 'funder-restrictions', tooltipField: 'restrictions', valueFormatter: (p) => p.value || '-' },
  { headerName: 'NOTES', field: 'notes', flex: 280, minWidth: 150, cellClass: 'funder-notes', tooltipField: 'notes', valueFormatter: (p) => p.value || '-',
    context: { editField: 'note', rowField: 'settings.note' } },
  // REG-828 (Zack 10/28 on REG-712): "Total Units" is every submission sent to
  // the funder, with the unique merchants behind them as a smaller number. On
  // this list its main number is the same count as Total Submissions Units;
  // filter and sort use that main number.
  { headerName: 'TOTAL UNITS', colId: 'totalUnits', field: 'totalSubmissions', type: 'rightAligned', flex: 150, minWidth: 170,
    headerTooltip: 'Submissions sent to this funder; the smaller number is how many distinct merchants they cover',
    filter: 'agNumberColumnFilter', cellClass: 'numeric',
    cellRenderer: (p: { data?: FunderRecord }) => p.data ? (
      <div className="funder-units-cell"><b>{p.data.totalSubmissions.toLocaleString()}</b><small>{p.data.totalUniqueMerchants.toLocaleString()} merchants</small></div>
    ) : null },
  { headerName: 'TOTAL SUBMISSIONS UNITS', field: 'totalSubmissions', type: 'rightAligned', flex: 150, minWidth: 190,
    filter: 'agNumberColumnFilter', cellClass: 'numeric' },
  { headerName: 'TOTAL APPROVALS UNITS', field: 'totalApprovals', type: 'rightAligned', flex: 140, minWidth: 180,
    headerTooltip: 'Approved, contracts or funded submissions (non-performing funded deals excluded)',
    filter: 'agNumberColumnFilter', cellClass: 'numeric' },
  { headerName: 'TOTAL DECLINES UNITS', field: 'totalDeclines', type: 'rightAligned', flex: 140, minWidth: 170,
    filter: 'agNumberColumnFilter', cellClass: 'numeric' },
  { headerName: 'TOTAL FUNDED UNITS', field: 'fundedCount', type: 'rightAligned', flex: 140, minWidth: 160,
    filter: 'agNumberColumnFilter', cellClass: 'numeric funded-number' },
  { headerName: 'TOTAL APPROVALS AMOUNT', field: 'totalApprovedAmount', type: 'rightAligned', flex: 150, minWidth: 190,
    filter: 'agNumberColumnFilter', cellClass: 'numeric', valueFormatter: (p) => dollars(Number(p.value) || 0) },
  { headerName: 'TOTAL DECLINES AMOUNT', field: 'totalDeclinedAmount', type: 'rightAligned', flex: 150, minWidth: 180,
    filter: 'agNumberColumnFilter', cellClass: 'numeric', valueFormatter: (p) => dollars(Number(p.value) || 0) },
  { headerName: 'TOTAL FUNDED AMOUNT', field: 'lifetimeFundedAmount', type: 'rightAligned', flex: 150, minWidth: 170,
    filter: 'agNumberColumnFilter', cellClass: 'numeric primary-cell', valueFormatter: (p) => dollars(Number(p.value) || 0) },
  { headerName: 'ACTIVE', colId: 'active', flex: 90, minWidth: 90, filter: 'agSetColumnFilter',
    valueGetter: (p) => (p.data ? (p.data.active ? 'Yes' : 'No') : '') },
  { headerName: 'GROSS COMM', field: 'grossCommission', type: 'rightAligned', flex: 130, minWidth: 115, hide: true,
    filter: 'agNumberColumnFilter', cellClass: 'numeric', valueFormatter: (p) => dollars(Number(p.value) || 0) },
  { headerName: 'APPROVAL → FUNDED', field: 'approvalToFundedRatio', type: 'rightAligned', flex: 140, minWidth: 135, hide: true,
    filter: 'agNumberColumnFilter', cellClass: 'numeric', valueFormatter: (p) => `${(Number(p.value) || 0).toFixed(1)}%` },
  { headerName: 'UNIQUE MERCHANTS', field: 'totalUniqueMerchants', type: 'rightAligned', flex: 135, minWidth: 125, hide: true,
    filter: 'agNumberColumnFilter', cellClass: 'numeric' },
];

const SUB_ID_COLUMN: ColDef<FunderSubmission> = {
  headerName: 'SUB ID', colId: 'subId', field: 'id', flex: 90, minWidth: 90, filter: 'agNumberColumnFilter',
  valueFormatter: (p) => (p.value != null ? `#${p.value}` : '-'),
};
const MAIN_REP_COLUMN: ColDef<FunderSubmission> = { headerName: 'MAIN REP', field: 'rep', flex: 160, minWidth: 120, tooltipField: 'rep' };
const MERCHANT_COLUMN: ColDef<FunderSubmission> = { headerName: 'BUSINESS NAME', field: 'merchant', flex: 200, minWidth: 150, cellClass: 'primary-cell', tooltipField: 'merchant' };
const STATUS_COLUMN: ColDef<FunderSubmission> = { headerName: 'STATUS', colId: 'status', flex: 150, minWidth: 130, filter: 'agSetColumnFilter',
  valueGetter: (p) => p.data ? FUNDER_STATUS_LABELS[p.data.decision] : '',
  cellRenderer: (p: { data?: FunderSubmission }) => p.data ? <span className={`rep-status ${p.data.decision}`}><i />{FUNDER_STATUS_LABELS[p.data.decision]}</span> : null };
const REASON_COLUMN: ColDef<FunderSubmission> = { headerName: 'DECLINE REASON / NOTE', field: 'reason', flex: 240, minWidth: 150, tooltipField: 'reason',
  cellClass: (p) => (p.data?.decision === 'declined' ? 'decline-reason' : ''), valueFormatter: (p) => p.value || '-' };
const AMOUNT_COLUMN: ColDef<FunderSubmission> = { headerName: 'AMOUNT', field: 'amount', type: 'rightAligned', flex: 120, minWidth: 110, filter: 'agNumberColumnFilter', cellClass: 'numeric primary-cell', valueFormatter: (p) => dollars(p.value) };
const FACTOR_COLUMN: ColDef<FunderSubmission> = { headerName: 'FACTOR', field: 'factor', type: 'rightAligned', flex: 100, minWidth: 90, filter: 'agNumberColumnFilter', cellClass: 'numeric', valueFormatter: (p) => (p.value != null ? Number(p.value).toFixed(2) : '-') };
const TERM_COLUMN: ColDef<FunderSubmission> = { headerName: 'TERM', field: 'term', type: 'rightAligned', flex: 90, minWidth: 80, filter: 'agNumberColumnFilter', cellClass: 'numeric', valueFormatter: (p) => (p.value != null ? String(p.value) : '-') };
const hidden = (column: ColDef<FunderSubmission>): ColDef<FunderSubmission> => ({ ...column, hide: true });

// REG-828: the detail tabs follow the COLUMNS sheet of the matching list
// (SUBMISSIONS, FUNDED, APPROVALS); columns the sheet does not list stay hidden.
const SHEET = sheetDealColumns<FunderSubmission>();
const FIRST_NAME_COLUMN: ColDef<FunderSubmission> = { headerName: 'FIRST NAME', field: 'merchantFirstName', flex: 130, minWidth: 110, valueFormatter: (p) => p.value || '-' };
const EMAIL_COLUMN: ColDef<FunderSubmission> = { headerName: 'EMAIL', field: 'merchantEmail', flex: 200, minWidth: 150, valueFormatter: (p) => p.value || '-' };
const FUNDER_SUBMISSION_COLUMNS: ColDef<FunderSubmission>[] = [
  SUB_ID_COLUMN,
  // REG-828: the Submissions list's Created At (long format); the old
  // activity date stays in the column chooser.
  SHEET.createdAt,
  MAIN_REP_COLUMN,
  MERCHANT_COLUMN,
  STATUS_COLUMN,
  REASON_COLUMN,
  AMOUNT_COLUMN,
  FACTOR_COLUMN,
  SHEET.payment,
  TERM_COLUMN,
  SHEET.commissionPct,
  SHEET.commission,
  SHEET.leadSource,
  SHEET.dealType,
  SHEET.brand,
  SHEET.contactMethod,
  { headerName: 'ACTIVITY DATE', field: 'date', flex: 110, minWidth: 100, valueFormatter: (p) => shortDate(p.value), hide: true },
];

const FUNDER_FUNDED_TAB_COLUMNS: ColDef<FunderSubmission>[] = [
  SUB_ID_COLUMN,
  { headerName: 'DATE FUNDED', field: 'dateFunded', flex: 120, minWidth: 110, valueFormatter: (p) => shortDate(p.value) },
  MAIN_REP_COLUMN,
  MERCHANT_COLUMN,
  SHEET.performance,
  FIRST_NAME_COLUMN,
  SHEET.phone,
  EMAIL_COLUMN,
  AMOUNT_COLUMN,
  FACTOR_COLUMN,
  TERM_COLUMN,
  SHEET.payment,
  SHEET.commissionPct,
  SHEET.commission,
  SHEET.psf,
  SHEET.leadSource,
  SHEET.dealType,
  SHEET.brand,
  SHEET.contactMethod,
  hidden(STATUS_COLUMN),
  hidden(REASON_COLUMN),
];
const FUNDER_APPROVAL_TAB_COLUMNS: ColDef<FunderSubmission>[] = [
  SUB_ID_COLUMN,
  { headerName: 'DATE APPROVED', field: 'approvalDate', flex: 130, minWidth: 120, valueFormatter: (p) => shortDate(p.value) },
  MAIN_REP_COLUMN,
  MERCHANT_COLUMN,
  FIRST_NAME_COLUMN,
  SHEET.phone,
  EMAIL_COLUMN,
  SHEET.state,
  AMOUNT_COLUMN,
  FACTOR_COLUMN,
  TERM_COLUMN,
  SHEET.payment,
  SHEET.commissionPct,
  SHEET.commission,
  // REG-740: live Merchant Feedback dropdown, same as the Approvals board.
  { headerName: 'MERCHANT FEEDBACK', colId: 'merchantResponse', field: 'merchantResponse', flex: 170, minWidth: 160,
    filter: 'agSetColumnFilter', cellClass: 'live-picklist-cell',
    cellRenderer: (p: { data?: FunderSubmission; value?: string | null }) => p.data ? (
      <LivePicklistCell
        target={MERCHANT_FEEDBACK_EDIT}
        recordId={p.data.merchantId}
        value={p.value ?? null}
        label="Merchant Feedback"
        onSaved={() => queryClient.invalidateQueries({ queryKey: ['funder-target-submissions'] })}
      />
    ) : null },
  SHEET.leadSource,
  SHEET.dealType,
  SHEET.brand,
  SHEET.contactMethod,
  hidden(STATUS_COLUMN),
  hidden(REASON_COLUMN),
];

type FunderDetailTab = 'funded' | 'submissions' | 'approvals' | 'applications';
const FUNDER_DETAIL_TABS: Array<{ id: FunderDetailTab; label: string }> = [
  { id: 'funded', label: 'Funded' },
  { id: 'submissions', label: 'Submissions' },
  { id: 'approvals', label: 'Approvals' },
  { id: 'applications', label: 'Applications' },
];

function FunderList({ funders, onOpen, createAction, onToggleActive }: { funders: FunderRecord[]; onOpen: (id: number) => void; createAction?: React.ReactNode; onToggleActive?: (funder: FunderRecord, active: boolean) => Promise<void> }) {
  const [search, setSearch] = useState('');
  // Active/Inactive/All choice remembered across sessions, defaulting to
  // active funders only — mirrors the rep list (REG-673).
  const [activeFilter, setActiveFilter] = useTableActiveFilter('funder');
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const query = search.trim().toLowerCase();
  const matchesActivity = (funder: FunderRecord) => activeFilter === 'all'
    || (activeFilter === 'active' ? funder.active : !funder.active);
  const handleToggle = async (funder: FunderRecord, active: boolean) => {
    if (!onToggleActive || togglingId != null) return;
    setTogglingId(funder.id);
    try {
      await onToggleActive(funder, active);
      message.success(active ? `${funder.name} marked active.` : `${funder.name} marked inactive.`);
    } catch (error) {
      message.error((error as Error).message || 'Unable to update active status.');
    } finally {
      setTogglingId(null);
    }
  };
  // Mirrors the rep list's ACTIVE column (REG-625); the shared grid ignores
  // row clicks on interactive targets, so the checkbox never opens the funder.
  const columns = useMemo<ColDef<FunderRecord>[]>(() => [
    ...FUNDER_COLUMNS,
    { headerName: 'ACTIVE', colId: 'active', width: 100, filter: 'agSetColumnFilter',
      cellClass: 'rep-active-cell',
      valueGetter: (p) => (p.data?.active ? 'Yes' : 'No'),
      cellRenderer: (p: { data?: FunderRecord }) => p.data ? (
        <input
          type="checkbox"
          className="rep-active-checkbox"
          aria-label={`${p.data.name} active`}
          checked={p.data.active}
          disabled={!onToggleActive || togglingId === p.data.id}
          onChange={(event) => void handleToggle(p.data!, event.target.checked)}
        />
      ) : null },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [onToggleActive, togglingId]);
  const inlineEdit = useTableInlineEdit<FunderRecord>({ tableName: 'funder', columns, invalidateQueryKeys: FUNDER_LIST_KEYS });
  const rows = funders.filter((funder) => matchesActivity(funder)
    && `${funder.name} ${funder.isoRep} ${funder.email} ${funder.notes}`.toLowerCase().includes(query));
  // Mini stats dashboard above the leaderboard + grid (REG-661/662). Header
  // totals come from the canonical funded summary (same numbers as the
  // Funded dashboard) — the per-funder view's commission formula drifts from
  // the canonical one, so summing rows disagrees with the rest of the app.
  const fundedSummary = useFundedSummary().data;
  const activeFunderCount = funders.filter((funder) => funder.active).length;
  return <>
    <div className="rep-stat-grid primary">
      <Stat large label="Lifetime Funded" value={fundedSummary ? shortDollars(fundedSummary.amount_funded) : '—'} sub={fundedSummary ? dollars(fundedSummary.amount_funded) : 'unique funded deals'} accent="var(--green)" />
      <Stat large label="Units Funded" value={fundedSummary ? fundedSummary.deals_funded.toLocaleString() : '—'} sub="unique funded deals" />
      <Stat large label="Gross Commission" value={fundedSummary ? shortDollars(fundedSummary.total_commission) : '—'} sub={fundedSummary ? dollars(fundedSummary.total_commission) : 'unique funded deals'} accent="var(--accent)" />
      <Stat large label="Average Deal" value={fundedSummary ? shortDollars(fundedSummary.average_deal) : '—'} sub="per funded deal" />
      <Stat large label="Active Funders" value={activeFunderCount.toLocaleString()} sub={`of ${funders.length} total`} />
    </div>
    <ListLeaderboard
      title="Top Funders"
      subtitle={`by funded units · ${activeFilter === 'all' ? 'all funders' : `${activeFilter} funders`} · all time`}
      entries={funders.filter(matchesActivity).map((funder) => ({
        id: funder.id,
        name: funder.name,
        rank: funder.fundedCount,
        tieBreak: funder.lifetimeFundedAmount,
        stats: [
          { label: 'Units', value: String(funder.fundedCount) },
          { label: 'Funded', value: shortDollars(funder.lifetimeFundedAmount), accent: 'var(--green)' },
          { label: 'Gross', value: shortDollars(funder.grossCommission), accent: 'var(--accent)' },
        ],
      }))}
      onOpen={onOpen}
    />
    <section className="funder-list-card">
    <div className="funder-toolbar"><label className="rep-search"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4-4"/></svg><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search funders, ISO reps..."/></label>
      <select
        className="rep-active-filter"
        aria-label="Filter funders by active status"
        value={activeFilter}
        onChange={(event) => setActiveFilter(event.target.value as ActiveFilterValue)}
      >
        <option value="active">Active funders</option>
        <option value="inactive">Inactive funders</option>
        <option value="all">All funders</option>
      </select>
      <span><b>{rows.length}</b> of {funders.length} funders</span>
      {inlineEdit.hasEditableColumns && (
        <>
          <UndoLastEditButton canUndo={inlineEdit.canUndo} nextUndoLabel={inlineEdit.nextUndoLabel} onClick={() => void inlineEdit.undoLast()} />
          <EditModeToggle active={inlineEdit.editMode} onClick={inlineEdit.toggleEditMode} />
        </>
      )}
      {createAction}</div>
    <WorkspaceGrid<FunderRecord>
      exportName="Funders"
      {...inlineEdit.gridProps}
      rows={rows}
      columns={inlineEdit.columns}
      getRowId={(funder) => String(funder.id)}
      onRowClick={(funder) => onOpen(funder.id)}
      storageKey="funder_list"
      rowHeight={52}
      emptyMessage="No funders match this search."
    />
  </section>
  </>;
}

function Stat({ label, value, sub, accent, large }: { label: string; value: React.ReactNode; sub: string; accent?: string; large?: boolean }) {
  return <div className={`rep-stat ${large ? 'large' : ''}`}><span className="rep-stat-label">{label}</span><b style={{ color: accent }}>{value}</b><small>{sub}</small></div>;
}

function Rate({ label, value, sub, accent }: { label: string; value: number; sub: string; accent: string }) {
  return <div className="rep-rate"><div className="rep-rate-ring" style={{ background: `conic-gradient(${accent} ${value * 3.6}deg,var(--border-soft) 0)` }}><span>{value}%</span></div><div><span className="rep-stat-label">{label}</span><small>{sub}</small></div></div>;
}

function SubmissionTable({ rows, onOpen, maxHeight, columns, rowHeight, storageKey }: { rows: FunderSubmission[]; onOpen: (row: FunderSubmission) => void; maxHeight: number; columns?: ColDef<FunderSubmission>[]; rowHeight?: number; storageKey?: string }) {
  const { data: metadata } = useGlobalMetadata();
  const prefetchSubmission = useCallback((row: FunderSubmission) => (
    prefetchRecordDestinationIfAbsent({ tableName: 'submission', id: row.id }, metadata)
  ), [metadata]);
  return <WorkspaceGrid<FunderSubmission>
    rows={rows}
    columns={columns ?? FUNDER_SUBMISSION_COLUMNS}
    getRowId={(row) => String(row.id)}
    onRowClick={onOpen}
    onRowIntent={prefetchSubmission}
    height={maxHeight}
    rowHeight={rowHeight}
    storageKey={storageKey}
    emptyMessage="No submissions match these filters."
  />;
}

function FunderDetail({ funder, rows, onBack, onSettingsSaved, onEditContact, onRename, onActiveChange }: { funder: FunderRecord; rows: FunderSubmission[]; onBack: () => void; onSettingsSaved: () => void; onEditContact?: () => void;
  onRename?: () => void; onActiveChange?: (active: boolean) => Promise<void> }) {
  const [savingActive, setSavingActive] = useState(false);
  const toggleActive = async (active: boolean) => {
    if (!onActiveChange || savingActive) return;
    setSavingActive(true);
    try {
      await onActiveChange(active);
      message.success(active ? `${funder.name} marked active.` : `${funder.name} marked inactive.`);
    } catch (error) {
      message.error((error as Error).message || 'Unable to update active status.');
    } finally {
      setSavingActive(false);
    }
  };
  const [status, setStatus] = useState('all'); const [month, setMonth] = useState('all'); const [year, setYear] = useState('all'); const [amount, setAmount] = useState('all'); const [reason, setReason] = useState('all');
  const [activeTab, setActiveTab] = useState<FunderDetailTab>('funded');
  // Dev-only Funded Dashboard for just this funder (the same tables as Admin > Reports).
  const [showDashboard, setShowDashboard] = useState(false);
  const canSeeDashboard = useMyCapabilities().hasCapability('edit_permissions');
  // 460px is the floor; the grid grows to the bottom of the visible content
  // area so zooming out shows more rows (REG-652).
  const submissionsFill = useFillGridHeight(460);
  // Per-table zoom, remembered across sessions (REG-653).
  const [tableZoom, setTableZoom] = useTableZoom('funder_detail_submissions');
  const { openRecord } = useRecordNavigator();
  const years = useMemo(() => [...new Set(rows.filter((row) => row.date).map((row) => new Date(row.date!).getFullYear()))].sort((a, b) => b - a), [rows]);
  const reasons = useMemo(() => [...new Set(rows.map((row) => row.declineReason).filter(Boolean))].sort(), [rows]); const bucket = AMOUNTS.find((item) => item.value === amount) || AMOUNTS[0];
  const filtered = useMemo(() => rows.filter((row) => { const d = row.date ? new Date(row.date) : null; return (status === 'all' || row.decision === status) && (month === 'all' || d?.getMonth() === Number(month)) && (year === 'all' || d?.getFullYear() === Number(year)) && row.amount >= bucket.min && row.amount < bucket.max && (reason === 'all' || row.declineReason === reason); }), [amount, bucket.max, bucket.min, month, reason, rows, status, year]);
  const current = useMemo(() => stats(filtered), [filtered]); const lifetime = useMemo(() => stats(rows), [rows]); const hasFilter = status !== 'all' || month !== 'all' || year !== 'all' || amount !== 'all' || reason !== 'all';
  const open = (row: FunderSubmission) => openRecord({ id: row.id, tableName: 'submission', label: row.merchant });
  const clear = () => { setStatus('all'); setMonth('all'); setYear('all'); setAmount('all'); setReason('all'); };

  // Tab row sets, each ordered most recent first on the tab's own date.
  const fundedRows = filtered.filter((row) => row.decision === 'funded')
    .sort((a, b) => new Date(b.dateFunded ?? b.date ?? 0).getTime() - new Date(a.dateFunded ?? a.date ?? 0).getTime());
  const approvalRows = filtered.filter((row) => row.isPrimary)
    .sort((a, b) => new Date(b.approvalDate ?? b.date ?? 0).getTime() - new Date(a.approvalDate ?? a.date ?? 0).getTime());
  // One row per merchant: the latest submission stands in for the application.
  const applicationRows = (() => {
    const seen = new Set<number | string>();
    return filtered.filter((row) => {
      const merchantKey = row.merchantId ?? `s${row.id}`;
      if (seen.has(merchantKey)) return false;
      seen.add(merchantKey);
      return true;
    });
  })();
  const tabRows: Record<FunderDetailTab, FunderSubmission[]> = {
    funded: fundedRows,
    submissions: filtered,
    approvals: approvalRows,
    applications: applicationRows,
  };
  const tabColumns: Partial<Record<FunderDetailTab, ColDef<FunderSubmission>[]>> = {
    funded: FUNDER_FUNDED_TAB_COLUMNS,
    approvals: FUNDER_APPROVAL_TAB_COLUMNS,
  };
  return <div className="rep-detail" data-testid="record-detail"><button className="rep-back" onClick={onBack}>‹ <span>All funders</span></button><header className="rep-detail-header"><span className="rep-detail-avatar funder-avatar">{funder.name.slice(0, 2).toUpperCase()}</span><div className="rep-detail-title"><h1 className="funder-title">{funder.name}{onRename && <ChipEditButton onClick={onRename} label={`Rename funder ${funder.name}`} />}</h1><p>ISO Rep <b>{funder.isoRep || '-'}</b></p></div><div className="rep-header-chips">
      <div className="rep-contact-chip">
        <small>EMAIL{onEditContact && <ChipEditButton onClick={onEditContact} label={`Edit ISO rep email for ${funder.name}`} />}</small>
        <b title={funder.email || undefined}>{funder.email || '-'}</b>
      </div>
      <div className="rep-contact-chip">
        <small>PHONE{onEditContact && <ChipEditButton onClick={onEditContact} label={`Edit ISO rep phone for ${funder.name}`} />}</small>
        <b>{funder.phone || '-'}</b>
      </div>
      <div className="rep-active-chip">
        <small>ACTIVE</small>
        <label title={onActiveChange ? `Mark ${funder.name} ${funder.active ? 'inactive' : 'active'}` : 'Read-only'}>
          <input
            type="checkbox"
            checked={funder.active}
            disabled={!onActiveChange || savingActive}
            onChange={(event) => void toggleActive(event.target.checked)}
          />
          <b className={funder.active ? 'accent' : ''}>{savingActive ? 'Saving…' : funder.active ? 'Yes' : 'No'}</b>
        </label>
      </div>
      <div className="funder-brand-chip"><small>SENDS AS</small><b className={funder.region ? 'accent' : ''}>{funder.region ? 'Region' : 'Skyline'}</b></div>
      <div><small>SIGNED UP</small><b>{shortDate(funder.createdAt)}</b></div><div><small>RELATIONSHIP</small><b className="accent">{tenure(funder.createdAt)}</b></div><div><small>LAST SUBMISSION</small><b>{shortDate(rows[0]?.date || null)}</b></div><div><small>LIFETIME FUNDED</small><b className="funded-number">{shortDollars(lifetime.fundedAmount)}</b></div></div></header>
    <div className="rep-filter-bar"><span className="rep-filter-label">☰ &nbsp; FILTERS</span><select value={status} onChange={(e) => setStatus(e.target.value)}><option value="all">All statuses</option><option value="funded">Funded</option><option value="approved">Approved</option><option value="declined">Declined</option><option value="no_response">No Response</option></select><select value={month} onChange={(e) => setMonth(e.target.value)}><option value="all">All months</option>{MONTHS.map((label, i) => <option key={label} value={i}>{label}</option>)}</select><select value={year} onChange={(e) => setYear(e.target.value)}><option value="all">All years</option>{years.map((value) => <option key={value}>{value}</option>)}</select><select value={amount} onChange={(e) => setAmount(e.target.value)}>{AMOUNTS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select><select value={reason} onChange={(e) => setReason(e.target.value)}><option value="all">Any decline reason</option>{reasons.map((value) => <option key={value}>{value}</option>)}</select>{hasFilter && <button onClick={clear}>Clear</button>}<span className="filter-count">Showing <b>{current.total}</b> of {rows.length} submissions</span></div>
    <div className="rep-stat-grid primary"><Stat large label="Total Submissions" value={current.total} sub={`${current.decided} decided`}/><Stat large label="Units Funded" value={current.funded} sub={`${current.funded} funded of ${current.approvedUnits} approved`}/><Stat large label="Amount Funded" value={shortDollars(current.fundedAmount)} sub={dollars(current.fundedAmount)} accent="var(--green)"/><Stat large label="Units Approved" value={current.approvedUnits} sub={`${current.approved} approved, not funded`} accent="var(--accent)"/><Stat large label="Approval Amount" value={shortDollars(current.approvalAmount)} sub={dollars(current.approvalAmount)} accent="var(--accent)"/></div>
    <div className="rep-stat-grid secondary"><Stat label="Declined Deals" value={current.declined} sub="lost submissions" accent="var(--red)"/><Stat label="No Response" value={current.noResponse} sub="awaiting / stale" accent="var(--amber)"/><Stat label="Avg Deal Size" value={shortDollars(current.average)} sub="per approval"/><Rate label="Approval Rate" value={current.approvalRate} sub={`${current.approvedUnits} approved / ${current.decided} decided`} accent="var(--green)"/><Rate label="Funded Rate" value={current.fundedRate} sub={`${current.funded} funded / ${current.approvedUnits} approved`} accent="var(--accent)"/></div>
    <section className="rep-data-card">
      <div className="rep-tabs" role="tablist">
        {FUNDER_DETAIL_TABS.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={!showDashboard && activeTab === tab.id}
            className={!showDashboard && activeTab === tab.id ? 'active' : ''}
            onClick={() => { setActiveTab(tab.id); setShowDashboard(false); }}
          >
            {tab.label} <em>{tabRows[tab.id].length}</em>
          </button>
        ))}
        {canSeeDashboard && <button role="tab" aria-selected={showDashboard} className={showDashboard ? 'active' : ''} onClick={() => setShowDashboard(true)}>Dashboard</button>}
        <span className="rep-tabs-note">{hasFilter ? 'filtered view' : 'all time'}</span>
        <GridZoomControl zoom={tableZoom} onChange={setTableZoom} />
      </div>
      {showDashboard ? <FundedDashboard embedded scope={{ kind: 'funder', id: funder.id }} /> : <div ref={submissionsFill.ref} style={{ ['--sky-grid-zoom' as string]: tableZoom }}>
        <SubmissionTable
          // One grid per tab, so each restores its own saved columns.
          key={activeTab}
          storageKey={`funder_detail_${activeTab}`}
          rows={tabRows[activeTab]}
          onOpen={open}
          maxHeight={submissionsFill.height}
          columns={tabColumns[activeTab]}
          rowHeight={Math.round(40 * tableZoom)}
        />
      </div>}
    </section>
    <FunderSettings funder={funder.settings} onSaved={onSettingsSaved}/>
  </div>;
}

export default function FunderPage(): React.ReactElement {
  const { openRecord, closeRecord } = useRecordNavigator();
  const selectedId = useRecordParam('funder');
  const fundersQuery = useQuery({ queryKey: ['funder-target-page'], queryFn: loadFunders, staleTime: 5 * 60_000 });
  const { hasCapability, isLoading: capabilitiesLoading } = useMyCapabilities();
  // Mirrors the RLS gate: funder UPDATE requires the edit_deals capability.
  const canEditFunders = !capabilitiesLoading && hasCapability('edit_deals');
  const [contactEditFunder, setContactEditFunder] = useState<FunderRecord | null>(null);
  const [renameFunder, setRenameFunder] = useState<FunderRecord | null>(null);
  const selected = selectedId ? fundersQuery.data?.find((funder) => funder.id === selectedId) : null;
  const detailQuery = useQuery({ queryKey: ['funder-target-submissions', selectedId], queryFn: () => loadFunderSubmissions(selectedId!), enabled: selectedId != null, staleTime: 2 * 60_000 });
  if (fundersQuery.isLoading || !fundersQuery.data) return <Loading error={fundersQuery.error as Error | null}/>;
  if (selectedId && !selected) return <Loading error={new Error('This funder no longer exists.')}/>;
  const setFunderActive = async (funderId: number, active: boolean) => {
    // .select('id') probes the write: RLS filters silently, so an empty result
    // means the row was not actually updated (same pattern as rep editing).
    const { data, error: updateError } = await (supabaseClient as any)
      .from('funder')
      .update({ is_active: active })
      .eq('id', funderId)
      .select('id');
    if (updateError) throw updateError;
    if (!data?.length) throw new Error('You may not have permission to change funder status.');
    await fundersQuery.refetch();
  };
  if (selected) {
    if (detailQuery.isLoading || !detailQuery.data) return <Loading error={detailQuery.error as Error | null}/>;
    return (
      <>
        <FunderDetail
          funder={selected}
          rows={detailQuery.data}
          onBack={closeRecord}
          onSettingsSaved={() => fundersQuery.refetch()}
          onEditContact={canEditFunders ? () => setContactEditFunder(selected) : undefined}
          onRename={canEditFunders ? () => setRenameFunder(selected) : undefined}
          onActiveChange={canEditFunders ? (active) => setFunderActive(selected.id, active) : undefined}
        />
        {contactEditFunder && (
          <FunderContactDialog
            funder={contactEditFunder}
            onClose={() => setContactEditFunder(null)}
            onSaved={() => fundersQuery.refetch()}
          />
        )}
        {renameFunder && (
          <FunderRenameDialog
            funder={renameFunder}
            onClose={() => setRenameFunder(null)}
            onSaved={async () => {
              // The detail tabs label rows by funder name, so refresh both.
              await Promise.all([fundersQuery.refetch(), detailQuery.refetch()]);
            }}
          />
        )}
      </>
    );
  }
  return (
    <FunderList
      funders={fundersQuery.data}
      onOpen={(id) => openRecord({ tableName: 'funder', id })}
      onToggleActive={canEditFunders ? (funder, active) => setFunderActive(funder.id, active) : undefined}
      createAction={(
        <CreateListRecordButton
          tableName="funder"
          onCreated={async (created) => {
            await fundersQuery.refetch();
            openRecord({ tableName: 'funder', id: created.id, label: created.label });
          }}
        />
      )}
    />
  );
}
